import React, {Component} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'

import Spinner from 'quiz-react-sdk/es/common/components/shared/spinner/Spinner'

import omit from '../../util/omit'
import Page from '../../components/page/Page'
import PageContent from '../../components/page/PageContent'
import SDKApp from '../../components/SDKApp'

import {
  getToken,
  getUsers,
  addUser,
  getCourses,
  getCourseSubaccounts,
  getAdminSubaccounts,
  getCourseAssignments,
  addCourse,
} from '../../actions/apiActions'

import {
  getShareableUsers,
  getUserInfo,
  handleShare,
  getShareableCourses,
  getCourseInfo,
  getShareableSubaccounts,
  getSubaccountInfo,
} from '../../services/bankSharingUtils'

import {SCOPE_BANKS_BUILD, SCOPE_OUTCOME_ALIGNMENT_SETS_CREATE} from '../../constants'
import {useNavigate} from 'react-router'

export class BanksInterfaceWrapper extends Component {
  static propTypes = {
    addUser: PropTypes.func.isRequired,
    addCourse: PropTypes.func.isRequired,
    assignmentSession: PropTypes.object,
    ariaLabel: PropTypes.string.isRequired,
    canvasContextId: PropTypes.number.isRequired,
    component: PropTypes.func.isRequired,
    contextType: PropTypes.string.isRequired,
    currentUser: PropTypes.shape({
      email: PropTypes.string,
      enrollment_names: PropTypes.arrayOf(PropTypes.string),
      full_name: PropTypes.string,
      uuid: PropTypes.string,
      account_uuid: PropTypes.string,
      bank_admin: PropTypes.bool,
    }),
    dispatchProps: PropTypes.object,
    features: PropTypes.array,
    getToken: PropTypes.func.isRequired,
    getUsers: PropTypes.func.isRequired,
    outcomesScope: PropTypes.string,
    scope: PropTypes.string,
    navigateToBuild: PropTypes.func.isRequired,
    returnToUrl: PropTypes.string,
    iceTopNavBarEnabled: PropTypes.bool,
    launchToken: PropTypes.string,
  }

  static defaultProps = {
    features: [],
    scope: SCOPE_BANKS_BUILD,
    outcomesScope: SCOPE_OUTCOME_ALIGNMENT_SETS_CREATE,
    returnToUrl: null,
    iceTopNavBarEnabled: false,
  }

  constructor(props) {
    super(props)

    if (!this.props.assignmentSession) {
      const {scope, outcomesScope, launchToken} = this.props
      this.props.getToken({scope, outcomesScope, launchToken})
    }

    /* eslint-disable immutable/no-mutation */
    this.getShareableUsers = getShareableUsers(props.getUsers)
    this.getUserInfo = getUserInfo(props.getUsers)
    this.handleShare = handleShare(props.addUser, props.addCourse)
    this.getShareableCourses = getShareableCourses(props.getCourses)
    this.getCourseInfo = getCourseInfo(props.getCourses)
    /* eslint-enable immutable/no-mutation */
  }

  fetchSubaccounts(rootAccountUuid) {
    if (
      this.props.currentUser &&
      this.props.currentUser.account_uuid === rootAccountUuid &&
      this.props.currentUser.enrollment_names.includes('admin') &&
      this.props.currentUser.bank_admin
    ) {
      return this.props.getAdminSubaccounts
    }

    return this.props.contextType === 'course'
      ? this.props.getCourseSubaccounts
      : this.props.getAdminSubaccounts
  }

  navigateToBuild = assignmentId => () => {
    this.props.navigateToBuild(assignmentId)
  }

  render() {
    if (!this.props.assignmentSession) {
      return <Spinner />
    }
    const {assignmentId, component, assignmentSession, itemBanksScope, returnToUrl} = this.props
    const {externalAccountUuid, host, token, outcomesHost, outcomesToken} = assignmentSession
    const Interface = component
    const interfaceProps = omit(this.props, Object.keys(BanksInterface.propTypes))

    // These need to be loaded once we can check the root account
    /* eslint-disable immutable/no-mutation */
    this.getSubaccountInfo = getSubaccountInfo(
      this.props.canvasContextId,
      this.fetchSubaccounts(externalAccountUuid),
    )
    this.getShareableSubaccounts = getShareableSubaccounts(
      this.props.canvasContextId,
      this.fetchSubaccounts(externalAccountUuid),
    )
    /* eslint-enable immutable/no-mutation */

    return (
      <Page>
        <SDKApp
          apiEndpoint={host}
          contextUuid={externalAccountUuid}
          externalAccountUuid={externalAccountUuid}
          itemBankSharingEnabled={this.props.features.includes('item_bank_sharing')}
          userToken={token}
          outcomesEndpoint={outcomesHost}
          outcomesToken={outcomesToken}
        >
          <PageContent ariaLabel={this.props.ariaLabel}>
            <Interface
              {...interfaceProps}
              storedRCEConfig={{}}
              currentUser={this.props.currentUser}
              getShareableCourses={this.getShareableCourses}
              getCourseInfo={this.getCourseInfo}
              getCourseAssignments={this.props.getCourseAssignments}
              getShareableUsers={this.getShareableUsers}
              getUserInfo={this.getUserInfo}
              getShareableSubaccounts={this.getShareableSubaccounts}
              getSubaccountInfo={this.getSubaccountInfo}
              minChars={3}
              navigateToBuild={assignmentId ? this.navigateToBuild(assignmentId) : null}
              onShare={this.handleShare}
              scope={itemBanksScope}
              returnToUrl={returnToUrl}
              iceTopNavBarEnabled={this.props.iceTopNavBarEnabled}
            />
          </PageContent>
        </SDKApp>
      </Page>
    )
  }
}

export function UnconnectedBanksInterface(props) {
  const navigate = useNavigate()

  function navigateToBank(bankId) {
    navigate(`/banks/${bankId}`)
  }

  function navigateToBanks() {
    navigate('/banks')
  }

  function navigateToBankEntry(bankEntryId, bankId) {
    navigate(`/banks/${bankId}/bank_entries/${bankEntryId}`)
  }

  function navigateToBuild(assignmentId) {
    navigate(`/build/${assignmentId}`)
  }

  function handleError() {
    navigate('/page-not-found')
  }

  return (
    <BanksInterfaceWrapper
      {...props}
      navigateToBank={navigateToBank}
      navigateToBanks={navigateToBanks}
      navigateToBankEntry={navigateToBankEntry}
      navigateToBuild={navigateToBuild}
      onError={handleError}
    />
  )
}

export function mapStateToProps({assignmentSessions, context, features, launchToken}, {scope}) {
  return {
    itemBanksScope: context.itemBanksScope || {
      uuid: null,
      type: 'user',
      title: null,
      launch_context_uuid: null,
      root_account_name: null,
    },
    assignmentId: context.assignmentId,
    currentUser: context.currentUser,
    features,
    assignmentSession: assignmentSessions[scope || SCOPE_BANKS_BUILD],
    returnToUrl: context.returnToUrl,
    contextType: context.contextType,
    canvasContextId: context.canvasContextId,
    iceTopNavBarEnabled: features.includes('ice_top_nav_bar'),
    launchToken: launchToken?.launchToken,
  }
}

export const BanksInterface = connect(mapStateToProps, {
  getCourses,
  getCourseSubaccounts,
  getAdminSubaccounts,
  getToken,
  getUsers,
  getCourseAssignments,
  addUser,
  addCourse,
})(UnconnectedBanksInterface)
